<template>
    <PageHeaderButtons :headerId="props.headerId"
        :buttons="buttons"
        :count="data.selectedItems?.length"
        :detail="!!data.selectedItems?.length"
        :tertiary="!!data.selectedItems?.length"
        :clearable="true"
        pluralizationCode="playlist"
        @clear="clearSelectedItems"
    />

    <div class="filters">
        <Field
            icon="search" sprite="actions"
            :placeholder="t('blocks.playlists.search')"
            class="noerror light"
            @change="onSearchChange"
        />

        <Filters
            :filters="filters"
            @change="setFilters"
        />

        <Sort :sort="PLAYLISTS_SORT"
            :code="data.sort?.code"
            @change="setSort" />
    </div>

    <div class="playlists-list">
        <Explorer :items="data.items"
            @dblclick="openItem"
            @select="onSelectItems"
            itemWidth="148px"
            ref="explorer"
        >
            <template v-slot="{ item, selected }">
                <PlaylistPreview :item="item"
                    :showState="true"
                    :disabled="props.disabledItems?.indexOf(item.id) !== -1"
                    v-context-menu="contextMenu"
                />
            </template>
        </Explorer>

        <LoadMore
            :loading="data.loading"
            :values="data.items"
            :total="data.total"
            :take="data.take"
            pluralization="playlist"
            @click="getItems"
        />

        <EmptyLabel
            v-if="!data.items.length && !data.loading"
            class="empty"
            :title="t(`pages.playlists.empty.title`)"
            :description="t(`pages.playlists.empty.description`)"
        />
    </div>
</template>

<script setup>
    import { useI18n } from 'vue-i18n';
    import { onMounted, reactive, ref, computed } from 'vue';
    // import api from '🔗/api';
    import Sort from '🔗/components/ui/Sort.vue';
    import Filters from '🔗/components/ui/Filters.vue';
    import Button from '🔗/components/ui/Button.vue';
    import LoadMore from '🔗/components/ui/LoadMore.vue';
    import Explorer from '🔗/components/ui/Explorer.vue';
    import EmptyLabel from '🔗/components/ui/EmptyLabel.vue';
    import PlaylistPreview from '🔗/components/ui/PlaylistPreview.vue';
    import Field from '🔗/components/ui/Field.vue';
    import KebabMenu from '🔗/components/ui/KebabMenu.vue';

    import PageHeaderButtons from '@/components/blocks/PageHeader/PageHeaderButtons.vue';

    // import stores from '@/stores';

    import { PLAYLISTS_SORT, PLAYLISTS_FILTERS } from '🔗/constants';


    const emit = defineEmits(['select', 'add', 'edit', 'editTracks']);
    const explorer = ref(null);

    const playlists = api.playlists();
    const modals = stores.modals();
    const locale = stores.locale();

    const { t } = useI18n();

    const props = defineProps({
        sort: { type: Array, default: () => ([]) },
        filters: { type: Array, default: () => ([]) },

        disabledItems: { type: Array, default: () => ([]) },

        canAdd: { type: Boolean, default: true },
        canRemove: { type: Boolean, default: true },
        canEdit: { type: Boolean, default: true },
        headerId: { type: [String, null], default: '#item-head-buttons' }
    });

    const data = reactive({
        items: [],
        loading: false,
        skip: 0,
        take: 30,
        total: 0,
        sort: null,
        searchStr: null,
        filters: {},

        addPlaylistOptions: Array.from({ length: 10 }, (_, i) => ({
            code: i + 1,
            name: t('pluralization.playlists.add', { count: i + 1, playlist: t('pluralization.playlists.playlist', i + 1) })
        }))
    });


    const buttons = computed(() => {
        const result = [];

        if (!data.selectedItems?.length) {
            if (props.canAdd) {
                result.push({
                    type: 'kebab',
                    name: t(`pages.playlists.add.title`),
                    icon: 'plus',
                    sprite: 'actions',
                    disabled: !playlists.canAdd({}),
                    items: data.addPlaylistOptions,
                    onClick: (item) => emit('add', item.code)
                })
            }

            return result;
        }

        if (data.selectedItems.some(playlist => playlists.canRemove({
            playlist
        })) && props.canRemove) {
            result.push({
                type: 'button',
                title: data.selectedItems.length > 1 ? t(`pages.playlists.remove-multiple.title`) : t(`pages.playlists.remove.title`),
                icon: 'trash',
                sprite: 'actions',
                onClick: removeSelectedItems
            });
        }

        if (data.selectedItems.length === 1 && props.canEdit) {
            result.push({
                type: 'button',
                name: t(`pages.playlists.edit-track.title`),
                icon: 'note',
                sprite: 'player',
                disabled: !playlists.canEditTracks({
                    playlist: data.selectedItems[0]
                }),
                onClick: editItemTracks
            });
        }

        if (data.selectedItems.some(playlist => playlists.canEdit({
                playlist
            }))) {
            result.push({
                type: 'button',
                name: data.selectedItems.length > 1 ? t(`pages.playlists.edit-multiple.title`) : t(`pages.playlists.edit.title`),
                icon: 'pen',
                sprite: 'actions',
                disabled: !props.canEdit,
                onClick: editSelectedItems
            });
        } else {
            result.push({
                type: 'button',
                name: data.selectedItems.length > 1 ? t(`pages.playlists.view-multiple.title`) : t(`pages.playlists.view.title`),
                icon: 'eye-opened',
                sprite: 'actions',
                disabled: !data.selectedItems.some(playlist => playlists.canView({
                    playlist
                })) || !props.canEdit,
                onClick: editSelectedItems
            });
        }

        return result;
    });

    const contextMenu = computed(() => {
        return buttons.value.filter(item => item.type === 'button').map(item => ({
            ...item,
            name: item.name || item.title
        })).reverse();
    });

    const filters = computed(() => {
        return PLAYLISTS_FILTERS
    });

    const setFilters = (payload) => {
        data.filters = payload;
        reload();
    }

    const setSort = (item) => {
        if (data.sort?.code === item.code) {
            data.sort = null;
        } else {
            data.sort = item;
        }

        reload();
    };

    const onSearchChange = async (e) => {
        data.searchStr = e.target.value?.toLowerCase().trim() || '';
        data.skip = 0;

        clearTimeout(data.searchTimeout);

        data.searchTimeout = setTimeout(() => {
            delete data.searchTimeout;
            reload();
        }, 200);
    }

    const getItems = async () => {
        data.loading = true;
        
        clearTimeout(data.setItemsTimeout);

        if (!data.skip) {
            data.items = [];
        }

        let params = {
            skip: data.skip,
            take: data.take
        };

        if (data.sort) {
            params.sort = data.sort.code;
            params.sort_dir = data.sort.direction;
        }

        if (!!data.searchStr?.trim().length) {
            params.title = data.searchStr.toLowerCase().trim();
        }

        if (data.filters && Object.keys(data.filters)?.length) {
            params = { ...data.filters, ...params };
        }

        const payload = await api.playlists().list(params) || [];

        data.setItemsTimeout = setTimeout(() => {
            if (payload?.items?.length) {
                data.items.splice(data.items.length, 0, ...payload.items);
            }

            data.skip = data.items.length;
            data.total = payload?.total || 0;

            data.loading = false;
        }, 200);
    };

    const reload = () => {
        data.skip = 0;
        getItems();
    };

    const addItem = (item) => {
        if (!item?.id) {
            return;
        }

        data.items.unshift(item)
        data.skip += 1;
        data.total += 1;
    }

    const updateItem = (payload) => {
        if (!payload?.id) {
            return;
        }

        const item = data.items.find(item => item.id === payload.id);
        if (item) {
            data.items.splice(data.items.indexOf(item), 1, payload);
        }
    }

    const removeItems = async (items, happened) => {
        if (!items?.length) {
            return;
        }

        // Удаляем лишние
        if (!happened) {
            await Promise.all(items.map(async (item) => {
                await playlists.remove(item.id);
            }));
        }

        const ids = items.map(item => item.id);

        const oldlength = data.items.length;
        data.items = data.items.filter(item => ids.indexOf(item.id) === -1);

        if (oldlength - data.items?.length > 0) {
            data.skip -= oldlength - data.items?.length;
            data.total -= oldlength - data.items?.length;
        }
    }

    const onSelectItems = (items) => {
        data.selectedItems = items;
        emit('select', items)
    }

    const openItem = (item) => {
        if (!item?.id) {
            return;
        }

        modals.reopen('playlist', {
            data: item.id,
            popover: true
        })
    }

    const removeSelectedItems = async () => {
        if (!data.selectedItems?.length) {
            return;
        }

        modals.confirm({
            title: t(`pages.playlists.remove-multiple.confirm.title`),
            body: t(`pages.playlists.remove-multiple.confirm.description`),
            onOk: async () => {
                await removeItems(data.selectedItems.filter(playlist => playlists.canRemove({
                        playlist
                    })));
                
                modals.open('success', {
                    data: {
                        title: t(`pages.playlists.remove-multiple.done.title`),
                        close: t(`pages.playlists.remove-multiple.done.close`)
                    },
                    quietClose: true
                });

                clearSelectedItems();
            }
        }); 
    }

    const editSelectedItems = () => {
        if (!data.selectedItems?.length) {
            return;
        }

        emit('edit', data.selectedItems);
    }

    const editItemTracks = () => {
        if (data.selectedItems?.length !== 1) {
            return;
        }

        emit('editTracks', data.selectedItems[0]);
    }

    const clearSelectedItems = () => {
        explorer?.value?.clearSelection();
        delete data.selectedItems;
        emit('select')
    }

    defineExpose({
        reload,
        updateItem,
        removeItems,
        addItem
    });

    onMounted(() => {
        getItems();
    });
</script>

<style lang="scss" scoped>
    .playlists-list {
        display: flex;
        flex: 1 1;
        flex-direction: column;

        .loader {
            flex-shrink: 0;
            margin: auto;
        }

        button {
            flex-shrink: 0;
        }
    }

    .filters {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 8px;
        justify-content: end;
        padding: 0 10px;
    }
</style>
